import { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import MultiImageUpload from "../../shared/MultiImageUpload/index.tsx";
import Switch from "../../shared/Switch/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

const convertToInputTime = (time?: string | null) => {
  if (!time) return "";
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = match[3].toUpperCase();
  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

const isVideoFile = (img: any) => {
  if (img?.type === "video") return true;
  if (img?.file?.type?.startsWith("video/")) return true;
  if (typeof img?.preview === "string") {
    return /\.(mp4|webm|mov|ogg|m4v)$/i.test(img.preview);
  }
  return false;
};

function CreateFilesUpload({ formik }: { formik: any }) {
  const [files, setFiles] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (fileList: File[]) => {
    const selected = fileList.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    const next = [...files, ...selected];
    setFiles(next);
    formik.setFieldValue("additionalFiles", next.map((f) => f.file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    formik.setFieldValue("additionalFiles", next.map((f) => f.file));
  };

  return (
    <Box
      className="multi-image-upload"
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={handleDrop}
    >
      <Box
        className="upload-zone"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => {
            if (e.target.files) addFiles(Array.from(e.target.files));
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <Box className="upload-zone-icon">
          <span className="material-symbols-outlined">cloud_upload</span>
        </Box>
        <Box className="upload-zone-text">
          <p>
            Drag and drop files here, or <span className="upload-link">browse</span> to choose files
          </p>
          <p style={{ marginTop: "8px", opacity: 0.7 }}>
            Supports JPG, PNG and MP4, WEBM up to 50MB
          </p>
        </Box>
      </Box>

      {files.length > 0 && (
        <Box className="image-gallery-grid">
          {files.map((img, index) => (
            <Box key={index} className="image-item">
              {isVideoFile(img) ? (
                <video
                  src={img.preview}
                  className="image-preview"
                  controls
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={img.preview}
                  alt={`Preview ${index + 1}`}
                  className="image-preview"
                />
              )}
              <Box className="image-overlay">
                <button
                  className="image-action-btn delete"
                  onClick={() => removeFile(index)}
                  title="Remove"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function EventCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      label: "",
      color: "#3b82f6",
      thumbnail_image: null,
      event_date: "",
      event_time: "",
      event_location: "",
      short_description: "",
      description: "",
      sort_order: "",
      isActive: "Active",
      sync_gallery: true,
      additionalFiles: [],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      thumbnail_image: isEdit
        ? Yup.mixed().nullable()
        : Yup.mixed().required("Image is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("label", values.label || "");
      formData.append("color", values.color || "#3b82f6");
      formData.append("event_date", values.event_date || "");
      formData.append("event_time", values.event_time || "");
      formData.append("event_location", values.event_location || "");
      formData.append("short_description", values.short_description || "");
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.thumbnail_image) {
        formData.append("thumbnail_image", values.thumbnail_image);
      }

      if (isEdit) {
        formData.append("_method", "PUT");
      } else {
        formData.append("sync_gallery", values.sync_gallery ? "true" : "false");
        (values.additionalFiles || []).forEach((file: File) => {
          formData.append("files[]", file);
        });
      }

      try {
        let response;
        if (isEdit) {
          response = await request.post("events/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("events/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Event updated successfully!" : "Event created successfully!");
          navigate(`/admin/events/${id || response.data.data?.id}`);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  const [eventFiles, setEventFiles] = useState<any[]>([]);

  const fetchEventFiles = async (eventId: string) => {
    try {
      const response = await request.get(`events/${eventId}/files`);
      setEventFiles(
        (response.data.data || []).map((f: any) => ({
          id: f.id,
          fileUrl: imagepathReturn(f.file_path),
          fileType: f.file_type,
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`events/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            label: item.label || "",
            color: item.color || "#3b82f6",
            thumbnail_image: null,
            event_date: item.event_date || "",
            event_time: convertToInputTime(item.event_time),
            event_location: item.event_location || "",
            short_description: item.short_description || "",
            description: item.description || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
            sync_gallery: false,
            additionalFiles: [],
          });
          setEditImagePreview(imagepathReturn(item.thumbnail_image || null));
          await fetchEventFiles(id as string);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Event" : "Create New Event"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/events"),
              }}
              primaryButton={{
                label: isEdit ? "Update Event" : "Create Event",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="label" label="Label" type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="event_date" label="Event Date" type="date" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="event_time" label="Event Time" type="time" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--3" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="event_location" label="Event Location" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="color"
                    label="Color"
                    type="color"
                    placeholder="#3b82f6"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="short_description"
                    label="Short Description"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="description"
                    label="Description"
                    type="text"
                    multiline
                    minRows={4}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="thumbnail_image"
                    formik={formik}
                    label="Upload Thumbnail Image"
                    editImagePreview={editImagePreview}
                  />
                </Box>
              </Box>

              <Box sx={{ marginTop: "24px" }}>
                <Typography sx={{ fontWeight: 600, color: "#1a1f3a", mb: 1.5 }}>
                  Event Files (Images & Videos)
                </Typography>
                {isEdit ? (
                  <MultiImageUpload
                    apiUrl={`events/${id}/files`}
                    payload={{ event_id: id }}
                    existingImages={eventFiles}
                    deleteApiUrl="event-files"
                    accept="image/*,video/*"
                    acceptText="Supports JPG, PNG and MP4, WEBM up to 50MB"
                    deleteApiRefetch={() => fetchEventFiles(id as string)}
                    onUploadSuccess={() => fetchEventFiles(id as string)}
                  />
                ) : (
                  <CreateFilesUpload formik={formik} />
                )}
              </Box>

              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "24px" }}
              >
                <Box className="form-field">
                  <Switch
                    name="sync_gallery"
                    label="Sync with Gallery"
                    formik={formik}
                    disabled={isEdit}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}